import React, { useEffect, useRef, useState } from "react";
import { Pages } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import MediaUpload from "@/components/MediaUpload";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2, RefreshCw, ExternalLink, Eye, EyeOff } from "lucide-react";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const IMAGE_FIELDS = new Set(["hero_image_url", "about_image_url"]);
const VIDEO_FIELDS = new Set(["intro_video_url"]);
const SLIDER_FIELDS = {
  hero_title_scale: { min: 0.5, max: 3.0, step: 0.05, suffix: "×" },
  hero_eyebrow_scale: { min: 0.5, max: 3.0, step: 0.05, suffix: "×" },
};
const NUMBER_FIELDS = { hero_countdown_hours: { min: 1, max: 168, step: 1, suffix: "hours" } };
const BOOLEAN_FIELDS = new Set(["hero_countdown_enabled"]);

// Section visibility toggles — appear at the top of the Home CMS editor.
// Each entry: [field_key, label, description]
const HOME_VISIBILITY_TOGGLES = [
  ["show_gallery", "Before & After gallery", "Show the before/after slider section on the home page. Photos are managed under Before & After — turn this off to hide the whole section."],
];

const SECTION_LABELS = {
  hero_eyebrow: "Hero — Eyebrow",
  hero_eyebrow_scale: "Hero — Eyebrow size (slider)",
  hero_title: "Hero — Title",
  hero_title_scale: "Hero — Title size (slider)",
  hero_countdown_enabled: "Hero — Show 48-hour countdown",
  hero_countdown_hours: "Hero — Countdown starts from (hours)",
  hero_countdown_label: "Hero — Countdown caption",
  hero_subtitle: "Hero — Subtitle",
  hero_primary_cta: "Hero — Primary CTA Text",
  hero_secondary_cta: "Hero — Secondary CTA Text",
  hero_tertiary_cta: "Hero — Third CTA Text (Contact)",
  hero_image_url: "Hero — Background image",
  about_eyebrow: "About — Eyebrow",
  about_title: "About — Title",
  about_body: "About — Body",
  about_image_url: "About / Why ATC — Side image",
  services_eyebrow: "Services — Eyebrow",
  services_title: "Services — Title",
  service_1_title: "Service 1 — Title",
  service_1_body: "Service 1 — Body",
  service_2_title: "Service 2 — Title",
  service_2_body: "Service 2 — Body",
  service_3_title: "Service 3 — Title",
  service_3_body: "Service 3 — Body",
  why_eyebrow: "Why — Eyebrow",
  why_title: "Why — Title",
  why_bullet_1: "Why — Bullet 1",
  why_bullet_2: "Why — Bullet 2",
  why_bullet_3: "Why — Bullet 3",
  why_bullet_4: "Why — Bullet 4",
  packages_eyebrow: "Packages — Eyebrow",
  packages_title: "Packages — Title",
  packages_subtitle: "Packages — Subtitle",
  testimonials_eyebrow: "Testimonials — Eyebrow",
  testimonials_title: "Testimonials — Title",
  gallery_eyebrow: "Gallery — Eyebrow",
  gallery_title: "Gallery — Title",
  gallery_subtitle: "Gallery — Subtitle",
  contact_eyebrow: "Contact — Eyebrow",
  contact_title: "Contact — Title",
  contact_phone: "Contact — Phone",
  contact_email: "Contact — Email",
  contact_address: "Contact — Address",
  footer_tagline: "Footer — Tagline",
  page_title: "Page Title",
  page_subtitle: "Page Subtitle",
  equipment_intro_html: "Equipment intro — appears between hero and package cards",
  intro_video_url: "Intro video pop-up (file or YouTube/Vimeo URL)",
  intro_video_caption: "Intro video caption — a short line shown alongside the video",
};

// Extra sections auto-injected into the editor per pageKey so admins can
// fill them in even when the DB record doesn't have them yet.
const EXTRA_SECTION_DEFAULTS = {
  packages: {
    intro_video_url: "",
    intro_video_caption: "",
  },
};

const parseNumeric = (raw) => {
  if (raw === undefined || raw === null) return "";
  const clean = String(raw).replace(/<[^>]*>/g, "").trim();
  const n = Number(clean);
  return Number.isFinite(n) ? n : "";
};

const SliderField = ({ value, onChange, config, testId }) => {
  const num = parseNumeric(value);
  const current = num === "" ? 1 : num;
  return (
    <div className="space-y-3">
      <input
        type="range"
        min={config.min}
        max={config.max}
        step={config.step}
        value={current}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-atc-blue"
        data-testid={testId}
      />
      <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
        <span>{config.min}{config.suffix}</span>
        <span className="text-white text-base font-bold">{current.toFixed(2)}{config.suffix}</span>
        <span>{config.max}{config.suffix}</span>
      </div>
    </div>
  );
};

const NumberField = ({ value, onChange, config, testId }) => {
  const num = parseNumeric(value);
  return (
    <div className="flex items-center gap-3">
      <Input
        type="number"
        min={config.min}
        max={config.max}
        step={config.step}
        value={num === "" ? "" : num}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="max-w-[140px] bg-slate-950 border-white/10 text-white"
        data-testid={testId}
      />
      {config.suffix && <span className="text-sm text-slate-400">{config.suffix}</span>}
    </div>
  );
};

const PageEditor = ({ pageKey }) => {
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState({});
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef(null);

  useEffect(() => {
    (async () => {
      const p = await Pages.get(pageKey);
      setPage(p);
      const extras = EXTRA_SECTION_DEFAULTS[pageKey] || {};
      setSections({ ...extras, ...(p.sections || {}) });
      setTitle(p.title || "");
    })();
  }, [pageKey]);

  if (!page) return <div className="p-6 text-slate-400">Loading…</div>;

  // Boolean-typed sections read as string "true"/"false" (RTE stores everything as string)
  const isSectionShown = (k) => sections[k] !== "false" && sections[k] !== false;
  const setSectionShown = (k, v) => setSections((s) => ({ ...s, [k]: v ? "true" : "false" }));

  const updateSection = (key) => (html) => {
    setSections((s) => ({ ...s, [key]: html }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await Pages.update(pageKey, { title, sections });
      toast.success("Saved — refreshing preview");
      setIframeKey((k) => k + 1);
      notifyPreviewRefresh();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = pageKey === "home" ? "/" : "/equipment";
  const keys = Object.keys(sections).filter((k) => !HOME_VISIBILITY_TOGGLES.some(([f]) => f === k));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="max-w-sm bg-slate-950/60 border-white/10 text-white"
          data-testid={`page-title-${pageKey}`}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border border-white/10 text-slate-200 hover:bg-slate-900/60"
            data-testid={`toggle-preview-${pageKey}`}
          >
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? "Hide preview" : "Show preview"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold hover:opacity-95 disabled:opacity-60"
            data-testid={`save-page-${pageKey}`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
        </div>
      </div>

      <div className={showPreview ? "grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6" : ""}>
        <div className="space-y-5">
          {pageKey === "home" && (
            <div className="glass rounded-xl p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-3">Section visibility</div>
              <div className="space-y-3">
                {HOME_VISIBILITY_TOGGLES.map(([k, label, desc]) => (
                  <div key={k} className="flex items-start gap-4">
                    <Switch
                      checked={isSectionShown(k)}
                      onCheckedChange={(v) => setSectionShown(k, v)}
                      data-testid={`toggle-section-${k}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">{label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            {keys.map((k) => (
              <div key={k} className="glass rounded-xl p-5">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">
                  {SECTION_LABELS[k] || k}
                </div>
                {IMAGE_FIELDS.has(k) ? (
                  <ImageUpload
                    value={sections[k] || ""}
                    onChange={updateSection(k)}
                    testId={`editor-${pageKey}-${k}`}
                    label={SECTION_LABELS[k] || k}
                  />
                ) : VIDEO_FIELDS.has(k) ? (
                  <MediaUpload
                    value={sections[k] || ""}
                    onChange={updateSection(k)}
                    testId={`editor-${pageKey}-${k}`}
                    kind="any"
                    label={SECTION_LABELS[k] || k}
                  />
                ) : BOOLEAN_FIELDS.has(k) ? (
                  <div className="flex items-center gap-3 py-2">
                    <Switch
                      checked={isSectionShown(k)}
                      onCheckedChange={(v) => setSectionShown(k, v)}
                      data-testid={`editor-${pageKey}-${k}`}
                    />
                    <span className="text-sm text-slate-300">
                      {isSectionShown(k) ? "Visible" : "Hidden"}
                    </span>
                  </div>
                ) : SLIDER_FIELDS[k] ? (
                  <SliderField
                    value={sections[k]}
                    onChange={(v) => setSections((s) => ({ ...s, [k]: String(v) }))}
                    config={SLIDER_FIELDS[k]}
                    testId={`editor-${pageKey}-${k}`}
                  />
                ) : NUMBER_FIELDS[k] ? (
                  <NumberField
                    value={sections[k]}
                    onChange={(v) => setSections((s) => ({ ...s, [k]: String(v) }))}
                    config={NUMBER_FIELDS[k]}
                    testId={`editor-${pageKey}-${k}`}
                  />
                ) : (
                  <RichTextEditor
                    value={sections[k] || ""}
                    onChange={updateSection(k)}
                    testId={`editor-${pageKey}-${k}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {showPreview && (
          <div className="hidden xl:block sticky top-6 self-start h-[calc(100vh-8rem)]">
            <div className="rounded-2xl border border-white/10 bg-slate-950/70 backdrop-blur-xl flex flex-col h-full overflow-hidden" data-testid={`page-live-preview-${pageKey}`}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-rose-500/60" />
                  <span className="h-2 w-2 rounded-full bg-amber-500/60" />
                  <span className="h-2 w-2 rounded-full bg-emerald-500/60" />
                  <div className="ml-3 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Live preview · {previewUrl}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setIframeKey((k) => k + 1)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white"
                    title="Refresh preview"
                    data-testid={`refresh-preview-${pageKey}`}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 rounded-md text-slate-400 hover:text-white"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
              <iframe
                ref={iframeRef}
                key={iframeKey}
                src={previewUrl + "?__preview=1"}
                title={`${pageKey} preview`}
                className="flex-1 w-full bg-slate-950"
                style={{ minHeight: "500px" }}
              />
              <div className="text-[10px] text-slate-500 px-4 py-2 border-t border-white/5">
                Click <span className="text-atc-blue">Save changes</span> to refresh — edits below appear here after saving.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PagesManager = () => {
  return (
    <div className="p-10" data-testid="pages-manager">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">CMS</div>
        <h1 className="font-display font-black text-4xl text-white">Pages</h1>
        <p className="text-slate-400 text-sm mt-2">Edit every text block on the public site. Use the toolbar for bold, italic, underline, colors, fonts, and images.</p>
      </div>

      <Tabs defaultValue="home">
        <TabsList className="bg-slate-950 border border-white/10">
          <TabsTrigger value="home" data-testid="tab-home">Home</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages-page">Packages page</TabsTrigger>
        </TabsList>
        <TabsContent value="home" className="mt-6">
          <PageEditor pageKey="home" />
        </TabsContent>
        <TabsContent value="packages" className="mt-6">
          <PageEditor pageKey="packages" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PagesManager;
